import { DEFAULT_WAVE_COLOR } from '@anm/constants/colors';
import useHover from '@anm/hooks/useHover';
import useIntersectionObserver from '@anm/hooks/useIntersectionObserver';
import React, { FC } from 'react';

import { ChatProps, VideoData, VideoPlayerCallbacks, VideoStatusCode } from '../../';
import Loader from '../Loader';
import Logo from '../Logo';
import TapToUnmute from '../TapToUnmute';

import VideoSources from './VideoSources';
import Wrapper, { PlayerWrapper, Preview } from './Wrapper';
import useAnalytic from './hooks/useAnalytic';
import useCleanXChar from './hooks/useCleanXChar';
import useClickToPlay from './hooks/useClickToPlay';
import useDisplayControls from './hooks/useDisplayControls';
import useDownloadSrc from './hooks/useDownloadSrc';
import useFullScreenQuality from './hooks/useFullScreenQuality';
import useShowLoader from './hooks/useShowLoader';
import useTapToUnmute from './hooks/useTapToUnmute';
import useThumbnailWidth from './hooks/useThumbnailWidth';
import useTimeUpdate from './hooks/useTimeUpdate';
import useVideoPlayer from './hooks/useVideoPlayer';
import useVideoPoster from './hooks/useVideoPoster';
import useWebinarVideo from './hooks/useWebinarVideo';
import useHLSVideo from '@anm/hooks/useHLSVideo';
import { GenericLiveStream } from 'types/stream';
import useLiveButton from './hooks/useLiveButton';
import ScheduledWebinar from '../ScheduledWebinar';
import formatDate from '@anm/helpers/date/formatDate';
import useChatControls from './hooks/useChatControls';
import usePlayInView from './hooks/usePlayInView';
import useCopyCurrentTime from './hooks/useCopyCurrentTime';
import usePlayerJS from '@anm/hosting-player/hooks/usePlayerJS';

export type VideoPlayerProps = VideoPlayerCallbacks & {
  videoId: string;
  videoData: VideoData;
  stream?: GenericLiveStream | null;
  chatProps?: ChatProps;
  startTime?: number;
  isPopover?: boolean;
  errorCode?: VideoStatusCode;
  canDestroy?: boolean;
  playerWidth?: number;
  webinarOwner?: string;
  isCustomization?: boolean;
  isChatMobileView?: boolean;
};

const VideoPlayer: FC<VideoPlayerProps> = props => {
  const {
    stream,
    errorCode,
    startTime,
    playerWidth,
    videoData,
    videoId,
    isPopover,
    chatProps,
    webinarOwner,
    canDestroy,
    onTimeChange,
    isChatMobileView,
    isCustomization
  } = props;

  const {
    sizes,
    configs,
    sources,
    customConfigs,
    bestQualityVideo,
    poster: { posterUrl }
  } = videoData;

  const colorWithDefault = customConfigs.playerColor || DEFAULT_WAVE_COLOR;
  const autoplay = !!startTime || isPopover || configs.autoplay || stream?.state === 'INPROGRESS';

  const isNoVideo = errorCode === VideoStatusCode.no_video;
  const [hoverRef, isHovered] = useHover<HTMLDivElement>();
  const [player, videoRef] = useVideoPlayer({
    ...props,
    startTime,
    sources,
    streamDuration: stream?.meta.duration
  });

  const [scrollAreaRef, inView] = useIntersectionObserver({ threshold: 0.4 }); // TODO: discuss treshold

  const [showPoster, setShowPoster] = useVideoPoster({
    startTime,
    player,
    posterUrl,
    configs
  });

  const [showTapIcon, handleUnmuteClick] = useTapToUnmute({
    player,
    autoplay,
    hideUnmute: configs.hideUnmute
  });

  const currentVideo = useDisplayControls(player, customConfigs.controls);
  const showLoader = useShowLoader(player, sources);
  const thumbnailWidth = useThumbnailWidth({
    sizes,
    player,
    currentVideo
  });

  useAnalytic(videoId, player);
  useCleanXChar(player);
  useDownloadSrc({ player, sources, name: videoData.meta.name });
  useTimeUpdate({ player, onTimeChange });
  useClickToPlay({ player, videoRef });
  usePlayInView({
    inView,
    player,
    autoplay,
    setShowPoster,
    playHidden: isCustomization || configs.playHidden
  });

  const isFullScreen = useFullScreenQuality({ player, sources });

  const [hlsUrl, showStartLabel, canCheckWebinar, canShowLiveLabel] = useWebinarVideo({
    stream,
    player,
    sources,
    canDestroy,
    setShowPoster
  });

  useHLSVideo({ videoRef, hlsVideo: hlsUrl, canRetry: canCheckWebinar });

  useChatControls({ player, currentVideo, chatProps, isChatMobileView });

  useLiveButton({
    stream,
    player,
    playerRef: currentVideo,
    canInject: canShowLiveLabel
  });

  const isVertical = sizes.width / sizes.height <= 1;

  useCopyCurrentTime({ player, playerRef: currentVideo, canRun: !isCustomization, isVertical });

  const showLogo = !!configs.playbar?.logo;
  const showDefaultPreview = isNoVideo && !posterUrl;
  const hideControls = !isHovered && configs.autoplay;

  const isFinishedStream = stream?.state === 'FINISHED' && !!stream.meta.endedAt && !player?.playing;

  const ownerName = 'host' || webinarOwner; // temporary

  const labelText =
    showStartLabel && ownerName
      ? `Waiting for ${ownerName} to start`
      : canDestroy
      ? 'Webinar is over'
      : isFinishedStream
      ? `This live has ended on </br> ${formatDate(new Date(stream!.meta.endedAt!).getTime(), {
          isReplaceValue: false
        })}`
      : null;

  usePlayerJS(videoRef);

  return (
    <div ref={scrollAreaRef}>
      <PlayerWrapper ref={currentVideo} playerColor={colorWithDefault}>
        <Wrapper
          {...{
            isNoVideo,
            isVertical,
            playerWidth,
            hideControls,
            thumbnailWidth,
            showDefaultPreview
          }}
          ref={hoverRef}
          key={bestQualityVideo}
          canCopyTimeLink={!isCustomization}
        >
          <video playsInline preload="none" crossOrigin="" ref={videoRef} poster={isFullScreen ? posterUrl : ''}>
            <VideoSources {...{ sources }} />
          </video>
          {showLogo && <Logo />}
          {showPoster && <Preview src={posterUrl} alt={videoData.meta.name || videoId} />}
          {showLoader && <Loader color={configs.playerColor} />}
          {showTapIcon && <TapToUnmute onClick={handleUnmuteClick} />}
          {labelText && showPoster && (
            <ScheduledWebinar title={labelText} scheduled={!isFinishedStream ? stream?.schedule : ''} />
          )}
        </Wrapper>
      </PlayerWrapper>
    </div>
  );
};

export default VideoPlayer;
